import React, { createContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';

// MUTATIONS
import CREATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/CREATE_CAMPAIGN_ORDER_REQUESTOR';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';
import UPDATE_SALESFORCE_OPPORTUNITY from '../../GraphQl/Mutations/UPDATE_SALESFORCE_OPPORTUNITY';
import CREATE_SALESFORCE_OPPORTUNITY from '../../GraphQl/Mutations/CREATE_SALESFORCE_OPPORTUNITY';
import CREATE_SALESFORCE_OPPORTUNITY_LINE_ITEMS from '../../GraphQl/Mutations/CREATE_SALESFORCE_OPPORTUNITY_LINE_ITEMS';
import CREATE_SALESFORCE_CONTACT from '../../GraphQl/Mutations/CREATE_SALESFORCE_CONTACT';
import CREATE_SALESFORCE_OPPORTUNITY_CONTACT_ROLE from '../../GraphQl/Mutations/CREATE_SALESFORCE_OPPORTUNITY_CONTACT_ROLE';
import UPDATE_SALESFORCE_OPPORTUNITY_FIELDS from '../../GraphQl/Mutations/UPDATE_SALESFORCE_OPPORTUNITY_FIELDS';
import SEND_PANDADOC_FROM_OPPORTUNITY from '../../GraphQl/Mutations/SEND_PANDADOC_FROM_OPPORTUNITY';
import UPSERT_CAMPAIGN_ORDER_CRM_DATA from '../../GraphQl/Mutations/UPSERT_CAMPAIGN_ORDER_CRM_DATA';
import UPSERT_CAMPAIGN_ORDER_ATTACHMENT from '../../GraphQl/Mutations/UPSERT_CAMPAIGN_ORDER_ATTACHMENT';
import UPDATE_CAMPAIGN_ORDER_SALESFORCE_ID from '../../GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_SALESFORCE_ID';

// QUERIES
import CURRENT_USER from '../../GraphQl/Queries/CURRENT_USER';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS';
import GET_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR';
import GET_CAMPAIGN_ORDERS_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDERS_REQUESTOR';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER';
import GET_BASIC_TAG_ACTIVITY from '../../GraphQl/Queries/GET_BASIC_TAG_ACTIVITY';
import GET_ORG_CRM_INTEGRATIONS from '../../GraphQl/Queries/GET_ORG_CRM_INTEGRATIONS';
import GET_ORG_SALESFORCE_NAME from '../../GraphQl/Queries/GET_SALESFORCE_ACCOUNT_NAME';
import GET_SALESFORCE_ACCOUNT_RECORD_TYPE from '../../GraphQl/Queries/GET_SALESFORCE_ACCOUNT_RECORD_TYPE';
import GET_SALESFORCE_ACCOUNT_NAME_DIRECT from '../../GraphQl/Queries/GET_SALESFORCE_ACCOUNT_NAME_DIRECT';
import GET_SALESFORCE_OPPORTUNITY_PROPOSAL_FILENAME from '../../GraphQl/Queries/GET_SALESFORCE_OPPORTUNITY_PROPOSAL_FILENAME';
import GET_SALESFORCE_OPPORTUNITY_PROPOSAL_NUMBER from '../../GraphQl/Queries/GET_SALESFORCE_OPPORTUNITY_PROPOSAL_NUMBER';
import GET_SALESFORCE_OPPORTUNITY_ID_BY_PROPOSAL_NUMBER from '../../GraphQl/Queries/GET_SALESFORCE_OPPORTUNITY_ID_BY_PROPOSAL_NUMBER';

import { CampaignConfig } from '../../../platform/ux/Campaigns/shared/configs/campaignConfig';

const CampaignRequestorContext = createContext({});

import sanitizePayload from '../../utils/sanitizePayload';

const wrapMutation = (mutateFn) => async (options) => {
  if (!options || !options.variables) {
    return mutateFn(options);
  }
  const sanitized = {
    ...options,
    variables: sanitizePayload(options.variables),
  };
  return mutateFn(sanitized);
};

export const CampaignRequestorProvider = (props) => {
  const { children } = props;
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [currentUserOrg, setCurrentUserOrg] = useState(null);
  const [campaignOrdersConnection, setCampaignOrdersConnection] = useState([]);
  const [kpis, setKpis] = useState(CampaignConfig.kpiDefaultOptions);
  const [tagActivityData, setTagActivityData] = useState(null);
  const [showActivityPopconfirm, setShowActivityPopconfirm] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const previewUserData = (() => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing userData from localStorage:', error);
      return null;
    }
  })();

  // QUERIES
  const [fetchChildrenOrgs, { data: dataChildrenOrgs, loading: loadingChildrenOrgs }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS, {
      onCompleted: (data) => {
      },
    });

  const [fetchSelectedAdvertiser] = useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER, {
    fetchPolicy: 'network-only', // Bypass cache to ensure latest pricingModel data
    onCompleted: ({ org }) => {
      setSelectedAdvertiser(org);
    },
  });

  const [fetchCampaignOrder, { data: dataCampaignOrder, loading: loadingCampaignOrder }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR, {
      fetchPolicy: 'network-only', // Bypass cache to ensure latest advertiser.pricingModel data
    });

  const [
    fetchCampaignOrdersConnection,
    { data: dataCampaignOrdersConnection, loading: loadingCampaignOrdersConnection },
  ] = useLazyQuery(GET_CAMPAIGN_ORDERS_REQUESTOR, {
    fetchPolicy: 'cache-and-network',
  });

  const [fetchOrgCRMIntegrations, { data: dataOrgCRMIntegrations }] = useLazyQuery(GET_ORG_CRM_INTEGRATIONS, {
    fetchPolicy: 'network-only',
  });

  const [fetchSalesforceAccountName, { data: dataSalesforceAccountName, loading: loadingSalesforceAccountName }] =
    useLazyQuery(GET_ORG_SALESFORCE_NAME, {
      fetchPolicy: 'network-only',
    });

  const [getBasicTagActivityDirect, { loading: loadingBasicTagActivity }] = useLazyQuery(GET_BASIC_TAG_ACTIVITY, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.getBasicTagActivity && data.getBasicTagActivity.retailID[0].transactions === 0) {
        setTagActivityData(data.getBasicTagActivity);
        setShowActivityPopconfirm(true);
      }
    },
  });

  const {
    data,
    loading: loadingCurrentUser,
    error: currentUserError,
  } = useQuery(CURRENT_USER, {
    onCompleted: ({ currentUser }) => {
      if (currentUser && currentUser.defaultRole) {
        const currentOrg =
          (previewUserData && previewUserData.role && previewUserData.role.org) ||
          currentUser.defaultRole.org;

        // Step 1: Immediately set advertiser so it shows up in the UI
        setSelectedAdvertiser(currentOrg);

        // Step 2: Fetch complete advertiser data including pricingModel
        // This will update selectedAdvertiser with the full data when it completes
        if (currentOrg && currentOrg.id) {
          fetchSelectedAdvertiser({
            variables: {
              where: { id: currentOrg.id }
            }
          });
        }
      }
    },
    onError: (error) => {
      console.error('Error fetching current user:', error);
    },
  });

  const currentUser = data?.currentUser;

  // MUTATIONS
  const [_createCampaignOrder, { loading: loadingCreateCampaignOrder }] = useMutation(
    CREATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      onCompleted: ({ createCampaignOrderRequestor }) => {
        fetchCampaignOrder({
          variables: {
            where: {
              id: createCampaignOrderRequestor.id,
            },
          },
        });
      },
    }
  );
  const createCampaignOrder = wrapMutation(_createCampaignOrder);

  const [_updateCampaignOrder, { loading: loadingUpdateCampaignOrder }] = useMutation(
    UPDATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      update: (cache, { data: { updateCampaignOrderRequestor } }) => {
        cache.writeQuery({
          query: GET_CAMPAIGN_ORDER_REQUESTOR,
          variables: {
            where: {
              id: updateCampaignOrderRequestor.id,
            },
          },
          data: {
            campaignOrder: updateCampaignOrderRequestor,
          },
        });
      },
    }
  );
  const updateCampaignOrder = wrapMutation(_updateCampaignOrder);

  const [updateSalesforceOpportunity, { loading: loadingUpdateSalesforceOpportunity }] = useMutation(
    UPDATE_SALESFORCE_OPPORTUNITY
  );

  // ─── create-opportunity.ts resolvers ───────────────────────────────────────
  const [createSalesforceOpportunity, { loading: loadingCreateSalesforceOpportunity }] =
    useMutation(CREATE_SALESFORCE_OPPORTUNITY);

  const [
    createSalesforceOpportunityLineItems,
    { loading: loadingCreateSalesforceOpportunityLineItems },
  ] = useMutation(CREATE_SALESFORCE_OPPORTUNITY_LINE_ITEMS);

  const [createSalesforceContact, { loading: loadingCreateSalesforceContact }] =
    useMutation(CREATE_SALESFORCE_CONTACT);

  const [
    createSalesforceOpportunityContactRole,
    { loading: loadingCreateSalesforceOpportunityContactRole },
  ] = useMutation(CREATE_SALESFORCE_OPPORTUNITY_CONTACT_ROLE);

  const [
    updateSalesforceOpportunityFields,
    { loading: loadingUpdateSalesforceOpportunityFields },
  ] = useMutation(UPDATE_SALESFORCE_OPPORTUNITY_FIELDS);

  const [sendPandaDocFromOpportunity, { loading: loadingSendPandaDocFromOpportunity }] =
    useMutation(SEND_PANDADOC_FROM_OPPORTUNITY);

  const [upsertCampaignOrderCRMData, { loading: loadingUpsertCampaignOrderCRMData }] =
    useMutation(UPSERT_CAMPAIGN_ORDER_CRM_DATA);

  const [upsertCampaignOrderAttachment, { loading: loadingUpsertCampaignOrderAttachment }] =
    useMutation(UPSERT_CAMPAIGN_ORDER_ATTACHMENT);

  const [updateCampaignOrderSalesforceId, { loading: loadingUpdateCampaignOrderSalesforceId }] =
    useMutation(UPDATE_CAMPAIGN_ORDER_SALESFORCE_ID);

  const [
    fetchSalesforceAccountRecordType,
    { data: dataSalesforceAccountRecordType, loading: loadingSalesforceAccountRecordType },
  ] = useLazyQuery(GET_SALESFORCE_ACCOUNT_RECORD_TYPE, { fetchPolicy: 'network-only' });

  const [
    fetchSalesforceAccountNameDirect,
    { data: dataSalesforceAccountNameDirect, loading: loadingSalesforceAccountNameDirect },
  ] = useLazyQuery(GET_SALESFORCE_ACCOUNT_NAME_DIRECT, { fetchPolicy: 'network-only' });

  const [
    fetchSalesforceOpportunityProposalFilename,
    {
      data: dataSalesforceOpportunityProposalFilename,
      loading: loadingSalesforceOpportunityProposalFilename,
    },
  ] = useLazyQuery(GET_SALESFORCE_OPPORTUNITY_PROPOSAL_FILENAME, { fetchPolicy: 'network-only' });

  const [
    fetchSalesforceOpportunityProposalNumber,
    {
      data: dataSalesforceOpportunityProposalNumber,
      loading: loadingSalesforceOpportunityProposalNumber,
    },
  ] = useLazyQuery(GET_SALESFORCE_OPPORTUNITY_PROPOSAL_NUMBER, { fetchPolicy: 'network-only' });

  const [
    fetchSalesforceOpportunityIdByProposalNumber,
    {
      data: dataSalesforceOpportunityIdByProposalNumber,
      loading: loadingSalesforceOpportunityIdByProposalNumber,
    },
  ] = useLazyQuery(GET_SALESFORCE_OPPORTUNITY_ID_BY_PROPOSAL_NUMBER, { fetchPolicy: 'network-only' });

  // TODO: This should be movd to user context
  useEffect(() => {
    if (currentUser) {
      const currentOrg =
        (previewUserData && previewUserData.role && previewUserData.role.org) ||
        currentUser.defaultRole.org;
      setCurrentUserOrg(currentOrg);
      if (currentOrg && currentOrg.id) {
        fetchOrgCRMIntegrations({ variables: { where: { id: currentOrg.id } } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (dataCampaignOrdersConnection && dataCampaignOrdersConnection.campaignOrdersConnection) {
      setCampaignOrdersConnection(
        dataCampaignOrdersConnection.campaignOrdersConnection.edges.map((edge) => edge.node)
      );
      setPagination({
        ...pagination,
        total: dataCampaignOrdersConnection.full.aggregate.count,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCampaignOrdersConnection]);

  return (
    <CampaignRequestorContext.Provider
      value={{
        currentUser,
        loadingCurrentUser,
        currentUserError,
        childrenOrg: dataChildrenOrgs ? dataChildrenOrgs.orgs : [],
        loadingChildrenOrgs,
        selectedAdvertiser,
        currentUserOrg,
        currentCampaignOrder: dataCampaignOrder ? dataCampaignOrder.campaignOrder : null,
        loadingCreateCampaignOrder,
        loadingCampaignOrder,
        loadingUpdateCampaignOrder,
        campaignOrdersConnection,
        loadingCampaignOrdersConnection,
        pagination,
        kpis,
        tagActivityData,
        showActivityPopconfirm,
        loadingBasicTagActivity,
        setShowActivityPopconfirm,
        setKpis,
        createCampaignOrder,
        updateCampaignOrder,
        updateSalesforceOpportunity,
        loadingUpdateSalesforceOpportunity,
        setSelectedAdvertiser,
        fetchCampaignOrder,
        fetchCampaignOrdersConnection,
        setPagination,
        fetchChildrenOrgs,
        fetchSelectedAdvertiser,
        getBasicTagActivityDirect,
        orgCRMIntegrations: dataOrgCRMIntegrations ? dataOrgCRMIntegrations.org : null,
        fetchSalesforceAccountName,
        salesforceAccountName: dataSalesforceAccountName ? dataSalesforceAccountName.getOrgSalesforceName : null,
        loadingSalesforceAccountName,

        // create-opportunity.ts resolvers
        createSalesforceOpportunity,
        loadingCreateSalesforceOpportunity,
        createSalesforceOpportunityLineItems,
        loadingCreateSalesforceOpportunityLineItems,
        createSalesforceContact,
        loadingCreateSalesforceContact,
        createSalesforceOpportunityContactRole,
        loadingCreateSalesforceOpportunityContactRole,
        updateSalesforceOpportunityFields,
        loadingUpdateSalesforceOpportunityFields,
        sendPandaDocFromOpportunity,
        loadingSendPandaDocFromOpportunity,
        // display-processor.ts resolvers
        upsertCampaignOrderCRMData,
        loadingUpsertCampaignOrderCRMData,
        upsertCampaignOrderAttachment,
        loadingUpsertCampaignOrderAttachment,
        updateCampaignOrderSalesforceId,
        loadingUpdateCampaignOrderSalesforceId,
        fetchSalesforceAccountRecordType,
        salesforceAccountRecordType:
          dataSalesforceAccountRecordType?.getSalesforceAccountRecordType ?? null,
        loadingSalesforceAccountRecordType,
        fetchSalesforceAccountNameDirect,
        salesforceAccountNameDirect:
          dataSalesforceAccountNameDirect?.getSalesforceAccountName ?? null,
        loadingSalesforceAccountNameDirect,
        fetchSalesforceOpportunityProposalFilename,
        salesforceOpportunityProposalFilename:
          dataSalesforceOpportunityProposalFilename?.getSalesforceOpportunityProposalFilename ?? null,
        loadingSalesforceOpportunityProposalFilename,
        fetchSalesforceOpportunityProposalNumber,
        salesforceOpportunityProposalNumber:
          dataSalesforceOpportunityProposalNumber?.getSalesforceOpportunityProposalNumber ?? null,
        loadingSalesforceOpportunityProposalNumber,
        fetchSalesforceOpportunityIdByProposalNumber,
        salesforceOpportunityIdByProposalNumber:
          dataSalesforceOpportunityIdByProposalNumber?.getSalesforceOpportunityIdByProposalNumber ?? null,
        loadingSalesforceOpportunityIdByProposalNumber,
      }}
      {...props}
    >
      {loadingCurrentUser ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      ) : (
        children
      )}
    </CampaignRequestorContext.Provider>
  );
};

export default CampaignRequestorContext;
